import React, {memo, useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";
import {
    Close,
    Image as ImageIcon,
    KeyboardDoubleArrowDown,
    KeyboardDoubleArrowUp,
    Send,
    SwapVert
} from "@mui/icons-material";
import {useInView} from "react-intersection-observer";
import useMachineStore from "../../Store/MachineStore";
import Constant from "../../Utils/Constant";
import {formatTaskName, updatePriorityTaskMachine} from "../../Service/TaskService";
import {transferTasks} from "../../Service/MachineService";
import {moveSelectionToPosition, toPriorityOrders} from "./queueOrder";
import {toggleId} from "./previewTasks";

const ROWS_PER_BATCH = 60;
const NO_IDS = [];
const JOURNAL_SOURCE = "organiser";

const areRowPropsEqual = (previous, next) => {
    return previous.task.id === next.task.id
        && previous.task.fileName === next.task.fileName
        && previous.task.referer === next.task.referer
        && previous.task.previewUrl === next.task.previewUrl
        && previous.position === next.position
        && previous.selected === next.selected
        && previous.onToggle === next.onToggle
        && previous.onPreview === next.onPreview
}

const QueueRow = memo(function QueueRow({task, position, selected, onToggle, onPreview}) {
    const name = formatTaskName(task)
    const isDtfDtf = task.referer && task.referer.toLowerCase() === "dtf-dtf"

    return (
        <Box
            data-task-id={task.id}
            sx={{
                display: "grid",
                gridTemplateColumns: "52px 56px minmax(0, 1fr) auto 56px",
                alignItems: "center",
                minHeight: 56,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: selected ? "action.selected" : "background.paper",
            }}
        >
            <Checkbox
                checked={selected}
                onChange={() => onToggle(task.id)}
                inputProps={{"aria-label": `Sélectionner ${name}`}}
            />
            <Typography sx={{fontVariantNumeric: "tabular-nums", fontWeight: 600, textAlign: "right", pr: 1.5}}>
                {position}
            </Typography>
            <ButtonBase onClick={() => onToggle(task.id)} sx={{justifyContent: "flex-start", minHeight: 56, minWidth: 0, textAlign: "left"}}>
                <Typography noWrap sx={{fontSize: 14}}>{name}</Typography>
            </ButtonBase>
            <Box sx={{px: 1}}>
                {task.referer &&
                    <Chip size="small" label={task.referer} sx={isDtfDtf ? {bgcolor: "#ff9800", color: "#000"} : undefined}/>
                }
            </Box>
            <IconButton
                onClick={() => onPreview(task)}
                disabled={!task.previewUrl}
                aria-label={`Aperçu de ${name}`}
                sx={{width: 48, height: 48}}
            >
                <ImageIcon/>
            </IconButton>
        </Box>
    )
}, areRowPropsEqual)

export default function QueueOrganizer({machineId, onClose, onPreviewFile}) {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down("lg"))
    const machines = useMachineStore(state => state.machines)
    const machine = machines.find(item => item.id === machineId)
    const [selectedIds, setSelectedIds] = useState(NO_IDS)
    const [visibleCount, setVisibleCount] = useState(ROWS_PER_BATCH)
    const [positionInput, setPositionInput] = useState("")
    const [targetAnchor, setTargetAnchor] = useState(null)
    const [pending, setPending] = useState(false)
    const [message, setMessage] = useState(null)
    const {ref: moreRowsRef, inView: moreRowsInView} = useInView({rootMargin: "400px 0px"})

    const waitingTasks = useMemo(() => {
        return (machine?.tasks || [])
            .filter(task => task.status === Constant.TASK_STATUS_WAITING)
            .sort((a, b) => a.priority - b.priority)
    }, [machine])

    const waitingIds = useMemo(() => waitingTasks.map(task => task.id), [waitingTasks])
    // Une tâche imprimée ou supprimée pendant que la vue est ouverte sort d'elle-même de la sélection.
    const selection = useMemo(() => selectedIds.filter(id => waitingIds.includes(id)), [selectedIds, waitingIds])
    const visibleTasks = useMemo(() => waitingTasks.slice(0, visibleCount), [waitingTasks, visibleCount])
    const hasMoreRows = visibleCount < waitingTasks.length
    const targetMachines = machines.filter(item => item.power && item.id !== machineId)

    useEffect(() => {
        if (moreRowsInView && hasMoreRows) {
            setVisibleCount(count => count + ROWS_PER_BATCH)
        }
    }, [moreRowsInView, hasMoreRows, visibleCount]);

    const handleToggle = useCallback((taskId) => {
        setSelectedIds(previous => toggleId(previous, taskId))
    }, [])

    if (!machine) {
        return null
    }

    const run = async (action, successText) => {
        setPending(true)
        try {
            await action()
            setSelectedIds(NO_IDS)
            setMessage({severity: "success", text: successText})
        } catch (error) {
            setMessage({severity: "error", text: `Déplacement non enregistré : ${error.message || "le serveur a refusé la demande"}`})
        } finally {
            setPending(false)
        }
    }

    const count = selection.length

    const handleMoveToEdge = (position) => {
        run(
            () => transferTasks(selection, position, {number: machine.number}, JOURNAL_SOURCE),
            position === "first" ? `${count} tâche(s) placée(s) en tête de la ${machine.number}` : `${count} tâche(s) placée(s) à la fin de la ${machine.number}`
        )
    }

    const handleMoveToPosition = () => {
        const position = parseInt(positionInput, 10)
        if (!Number.isInteger(position) || position < 1) {
            setMessage({severity: "error", text: "Indiquez une position à partir de 1"})
            return
        }

        const orders = toPriorityOrders(moveSelectionToPosition(waitingIds, selection, position))
        const finalPosition = Math.min(position, waitingIds.length - count + 1)
        run(async () => {
            const result = await updatePriorityTaskMachine({
                typeDrop: "changePriority",
                targetMachineId: machine.id,
                orders,
                source: JOURNAL_SOURCE,
            })
            if (result.response !== "success") {
                throw new Error(result.error)
            }
        }, `${count} tâche(s) déplacée(s) en position ${finalPosition}`)
        setPositionInput("")
    }

    const handleSendTo = (target, position) => {
        setTargetAnchor(null)
        run(
            () => transferTasks(selection, position, {number: target.number}, JOURNAL_SOURCE),
            `${count} tâche(s) envoyée(s) ${position === "first" ? "en tête" : "à la fin"} de la ${target.number}`
        )
    }

    const actionDisabled = count === 0 || pending

    return (
        <Dialog
            open
            fullScreen={fullScreen}
            fullWidth
            maxWidth="md"
            onClose={onClose}
            PaperProps={{sx: {height: fullScreen ? "100%" : "90vh"}}}
        >
            <DialogTitle sx={{display: "flex", alignItems: "center", gap: 2, pr: 1.5}}>
                <Box sx={{flexGrow: 1, minWidth: 0}}>
                    <Typography variant="h6" component="span" noWrap sx={{display: "block"}}>
                        Organiser la file · {machine.number} - {machine.name}
                    </Typography>
                    <Typography variant="body2" component="span" color="text.secondary">
                        {waitingTasks.length} tâche(s) en attente · cochez des tâches puis choisissez où les placer
                    </Typography>
                </Box>
                <IconButton onClick={onClose} aria-label="Fermer" sx={{width: 52, height: 52}}>
                    <Close/>
                </IconButton>
            </DialogTitle>

            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                    px: 3,
                    py: 1.5,
                    borderTop: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "background.default",
                }}
            >
                <Typography sx={{fontWeight: 600, minWidth: 140}}>
                    {count === 0 ? "Aucune sélection" : `${count} sélectionnée(s)`}
                </Typography>
                <Button variant="contained" size="large" startIcon={<KeyboardDoubleArrowUp/>} disabled={actionDisabled}
                        onClick={() => handleMoveToEdge("first")}>
                    En tête
                </Button>
                <Button variant="outlined" size="large" startIcon={<KeyboardDoubleArrowDown/>} disabled={actionDisabled}
                        onClick={() => handleMoveToEdge("last")}>
                    À la fin
                </Button>
                <Stack direction="row" gap={1} alignItems="center">
                    <TextField
                        id="organiser-position"
                        size="small"
                        label="Position"
                        value={positionInput}
                        onChange={(event) => setPositionInput(event.target.value.replace(/\D/g, ""))}
                        inputProps={{inputMode: "numeric"}}
                        sx={{width: 110}}
                    />
                    <Button variant="outlined" size="large" startIcon={<SwapVert/>} disabled={actionDisabled || !positionInput}
                            onClick={handleMoveToPosition}>
                        Déplacer
                    </Button>
                </Stack>
                <Button variant="outlined" size="large" startIcon={<Send/>} disabled={actionDisabled}
                        onClick={(event) => setTargetAnchor(event.currentTarget)}>
                    Envoyer vers…
                </Button>
                {count > 0 &&
                    <Button size="large" disabled={pending} onClick={() => setSelectedIds(NO_IDS)}>
                        Désélectionner
                    </Button>
                }
                {pending && <CircularProgress size={24}/>}
            </Box>

            <DialogContent sx={{p: 0}}>
                {waitingTasks.length === 0 &&
                    <Alert severity="info" sx={{m: 3}}>Aucune tâche en attente sur la {machine.number}</Alert>
                }

                {visibleTasks.map((task, index) => (
                    <QueueRow
                        key={task.id}
                        task={task}
                        position={index + 1}
                        selected={selection.includes(task.id)}
                        onToggle={handleToggle}
                        onPreview={onPreviewFile}
                    />
                ))}

                {hasMoreRows &&
                    <Box ref={moreRowsRef} sx={{textAlign: "center", py: 2}}>
                        <CircularProgress size={28}/>
                    </Box>
                }
            </DialogContent>

            <Menu anchorEl={targetAnchor} open={Boolean(targetAnchor)} onClose={() => setTargetAnchor(null)}>
                {targetMachines.length === 0 &&
                    <MenuItem disabled>Aucune autre machine allumée</MenuItem>
                }
                {targetMachines.map(target => (
                    <MenuItem key={target.id} disableRipple sx={{gap: 2, cursor: "default", minHeight: 56}}>
                        <Typography sx={{flexGrow: 1}}>{target.number} - {target.name}</Typography>
                        <Button variant="contained" onClick={() => handleSendTo(target, "first")}>En tête</Button>
                        <Button variant="outlined" onClick={() => handleSendTo(target, "last")}>À la fin</Button>
                    </MenuItem>
                ))}
            </Menu>

            <Snackbar
                open={Boolean(message)}
                autoHideDuration={4000}
                onClose={() => setMessage(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert severity={message?.severity || "info"} variant="filled" onClose={() => setMessage(null)} sx={{width: "100%"}}>
                    {message?.text || ""}
                </Alert>
            </Snackbar>
        </Dialog>
    )
}
